import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAcceptProductsMutation } from '@app/api/Products';
import type {
  ModelProduct,
  ModelProductAcceptance,
  ModelProductsAcceptance,
} from '@app/api/Products';
import { WAREHOUSE_OPERATIONS_PATH } from '@app/constants';
import { useWarehouseOperationData } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';

const { useForm, useWatch } = Form;

export const useProductOperation = () => {
  const {
    ActiveBranchId,
    products,
    warehousesOptions,
    suppliersOptions,
    isPageLoading,
    errors,
  } = useWarehouseOperationData();

  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [form] = useForm<ModelProductsAcceptance>();
  const formValues = useWatch([], form) ?? 0;
  const { setFieldValue } = form;
  const [selectedProducts, setSelectedProducts] = useState<ModelProduct[]>([]);
  const [isOpenAddProductsModal, setIsOpenAddProductsModal] = useState(false);

  const [acceptProducts, { isLoading: isLoadingAcceptProducts }] =
    useAcceptProductsMutation();

  useErrorHandler(errors);

  const openAddProductsModal = useCallback(() => {
    setIsOpenAddProductsModal(true);
  }, []);

  const closeAddProductsModal = useCallback(() => {
    setIsOpenAddProductsModal(false);
  }, []);

  const totals = useMemo(() => {
    const list = formValues?.Products ?? [];
    return list.reduce(
      (acc, cur) => {
        const cnt = Number(cur?.Count ?? 0);
        const price = Number(cur?.PriceOfPurchase ?? 0);
        acc.count += cnt;
        acc.sum += cnt * price;
        return acc;
      },
      { count: 0, sum: 0 }
    );
  }, [formValues]);

  const onAdd = useCallback(
    (selected: ModelProduct[]) => {
      const products: ModelProductAcceptance[] = selected.map(({ Id }) => ({
        ProductId: Id,
        Count: 1,
        PriceOfPurchase: 0,
      }));
      setSelectedProducts(selected);
      setFieldValue('Products', products);
      closeAddProductsModal();
    },
    [closeAddProductsModal, setFieldValue]
  );

  const onChangeTotalPrice = useCallback(
    (value: number | null, name: number) => {
      const totalPrice = value ?? 0;
      const list = formValues?.Products ?? [];
      const product = list[name] ?? {};
      const count = product.Count ?? 1;
      const priceOfPurchase = count ? totalPrice / count : 0;
      setFieldValue(['Products', name, 'PriceOfPurchase'], priceOfPurchase);
    },
    [formValues, setFieldValue]
  );

  const acceptProductsHandler = useCallback(
    async (data: ModelProductsAcceptance) => {
      try {
        await acceptProducts({ data, ActiveBranchId }).unwrap();
        setSelectedProducts([]);
        setFieldValue('Products', []);
        closeAddProductsModal();
        showNotification('Приход успешно оформлен', 'success');
        navigate(WAREHOUSE_OPERATIONS_PATH);
      } catch (error) {
        showNotification(error);
      }
    },
    [
      acceptProducts,
      setFieldValue,
      closeAddProductsModal,
      showNotification,
      ActiveBranchId,
      navigate,
    ]
  );

  const onDelete = useCallback((productId: number) => {
    setSelectedProducts((prev) => prev.filter((p) => p.Id !== productId));
  }, []);

  return {
    form,
    isOpenAddProductsModal,
    openAddProductsModal,
    closeAddProductsModal,
    onAdd,
    isPageLoading,
    warehousesOptions,
    suppliersOptions,
    products,
    selectedProducts,
    setSelectedProducts,
    formValues,
    onChangeTotalPrice,
    totals,
    acceptProductsHandler,
    onDelete,
    isLoadingAcceptProducts,
  };
};
